<template>
  <b-modal
    :active="true"
    has-modal-card
    full-screen
    :can-cancel="false"
    custom-class="standup__modal-background"
  >
    <div v-if="isLoading" style="position: relative; min-height: 150px">
      <b-loading v-if="isLoading" :is-full-page="false" :active.sync="isLoading"></b-loading>
    </div>
    <div v-else class="modal-card">
      <nav
        class="navbar is-fixed-top team-standup__navigation"
        role="navigation"
        aria-label="standup navigation"
      >
        <div class="navbar-menu">
          <div class="navbar-start">
            <div class="navbar-item">
              <user-avatar
                :user="users[activeTeam.teamLeaderId]"
                size="small"
                custom-class="u-m-r-small"
              />
              <b-tag type="is-primary-light" rounded>Team Leader</b-tag>
            </div>
          </div>
          <div class="navbar-end">
            <div class="navbar-item">
              <b-button
                type="is-text"
                @click="handleClickLeaveStandup"
                @keyup.enter="handleClickLeaveStandup"
              >
                Leave
              </b-button>
            </div>
          </div>
        </div>
      </nav>
      <section class="section modal-card-body">
        <h5 class="title is-5">
          {{ activeTeam.teamName }}
          <span class="is-size-6 has-text-weight-normal">({{ activeTeamUsers.length }} Members)</span>
        </h5>
        <p class="subtitle">
          {{ new Date().toLocaleString(undefined, defaultDateOptions) }}
        </p>
        <div class="card">
          <header
            v-if="activeStandup.hasReportingStarted && !activeStandup.isStandupReportingComplete"
            class="card-header"
          >
            <span class="card-header-title">
              <user-avatar :user="users[activeStandup.currentReportingUserId]" size="small" />
              &nbsp;&nbsp;
              <user-display-name :active-user-id="activeStandup.currentReportingUserId" size="small" />
              <b-tag v-if="isCurrentReporterOOO" type="is-warning" class="u-m-l-small">OOO</b-tag>
            </span>
          </header>
          <div
            v-if="!activeStandup.hasReportingStarted && !activeStandup.isStandupReportingComplete"
            class="card-content has-text-centered"
          >
            <h5 class="title is-5">Welcome to standup!</h5>
            <b-button
              type="is-info"
              @click.prevent="handleClickBeginReporting"
              @keyup.enter="handleClickBeginReporting"
            >
              Begin Reporting
            </b-button>
          </div>
          <div
            v-else-if="activeStandup.hasReportingStarted && !activeStandup.isStandupReportingComplete"
            class="card-content"
          >
            <b-message v-if="isCurrentReporterOOO" type="is-warning" :closable="false" class="u-m-b">
              <user-display-name :active-user-id="activeStandup.currentReportingUserId" size="small" /> is out of office today.
            </b-message>
            <daily-goal
              v-for="teamReportingDate in teamReportingDatesInISO"
              :key="teamReportingDate.key"
              :activeUserId="activeStandup.currentReportingUserId"
              :goalDate="teamReportingDate"
              is-explanation-expanded-by-default
            />
          </div>
          <div v-else-if="activeStandup.isStandupReportingComplete" class="card-content has-text-centered">
            <h5 class="title is-5">Standup reporting completed!</h5>
            <p class="u-m-b-large">Does anyone have any questions, comments, announcements or big wins?</p>
            <b-button
              type="is-info"
              @click.prevent="handleClickLeaveStandup"
              @keyup.enter="handleClickLeaveStandup"
            >
              Leave Standup
            </b-button>
          </div>
        </div>
      </section>
      <footer class="team-standup__footer">
        <!--<div class="team-standup__footer__section team-standup__footer__timer__wrapper">
          <div class="team-standup__footer__timer">
            {{ (standupTimerMinutes.toString())
                .padStart(2, '0') }}:{{ (standupTimerSeconds.toString()).padStart(2, '0')
            }}
          </div>
        </div>-->
        <div class="team-standup__footer__users-queue__wrapper">
          <span
            v-for="user in alreadyReportedUsers"
            :key="user.id" class="users-queue__avatar"
          >
            <b-icon
              v-if="activeStandup.currentReportingUserId === user.id"
              icon="menu-down"
              type="is-primary"
              custom-class="users-queue__reporting-icon"
            />
            <user-avatar
              :key="user.id"
              :user="user"
              size="small"
              is-clickable
              is-faded
              @click.native="handleSelectReportingUser(user.id)"
            />
            <span v-if="isUserOOO(user, todayISO)" class="users-queue__ooo-badge">OOO</span>
          </span>
          <span v-if="alreadyReportedUsers.length" class="users-queue__divider" />
          <b-button
            v-if="!activeStandup.isStandupReportingComplete && activeTeamUsers.length >= 2"
            type="is-primary"
            size="is-small"
            class="users-queue__shuffle-button"
            :class="{ 'is-shuffling': isShuffling }"
            :disabled="isShuffling"
            @click="handleClickShuffle"
            @keyup.enter="handleClickShuffle"
          >
            <b-icon icon="shuffle" size="is-small" />
            <span>Shuffle</span>
          </b-button>
          <!--
            transition-group gives us FLIP for free: whenever displayedPendingUsers reorders,
            each avatar glides to its new slot instead of jumping. The riffle animation just
            feeds it a few temporary random orders before the real one.
          -->
          <transition-group name="queue" tag="span" class="users-queue__list">
            <span
              v-for="user in displayedPendingUsers"
              :key="user.id"
              class="users-queue__avatar"
              :class="{ 'users-queue__avatar--landed': landedUserId === user.id }"
            >
              <b-icon
                v-if="activeStandup.currentReportingUserId === user.id"
                icon="menu-down"
                type="is-primary"
                custom-class="users-queue__reporting-icon"
              />
              <user-avatar
                :key="user.id"
                :user="user"
                size="small"
                is-clickable
                @click.native="handleSelectReportingUser(user.id)"
              />
              <span v-if="isUserOOO(user, todayISO)" class="users-queue__ooo-badge">OOO</span>
            </span>
          </transition-group>
        </div>
        <div
          v-if="activeStandup.hasReportingStarted && !activeStandup.isStandupReportingComplete"
          class="team-standup__footer__actions"
        >
          <div class="team-standup__footer__end">
            <!--
              No @keyup.enter on the two navigation buttons: b-button renders a native <button>,
              which already fires click on Enter, so the extra handler would move two people at once.
            -->
            <b-button
              type="is-text"
              class="u-m-r"
              icon-left="arrow-left"
              :disabled="!alreadyReportedUsers.length || isAdvancing"
              @click="goToPreviousReporter"
            >
              Back
            </b-button>
            <b-button
              v-if="isCurrentReporterOOO"
              type="is-warning"
              outlined
              class="u-m-r"
              @click="handleClickSkipOOO"
              @keyup.enter="handleClickSkipOOO"
            >
              Skip (OOO)
            </b-button>
            <b-button
              type="is-dark"
              outlined
              class="u-m-r"
              @click="handleClickEndReporting"
              @keyup.enter="handleClickEndReporting"
            >
              End Reporting
            </b-button>
            <b-button
              type="is-primary"
              icon-right="arrow-right"
              :loading="isAdvancing"
              @click="advanceToNextReporter"
            >
              {{ nextReporterButtonLabel }}
            </b-button>
          </div>
          <p v-if="isPresenter" class="team-standup__footer__clicker-hint">
            Tip: use &larr; / &rarr; or a presentation clicker to move between people.
          </p>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} from 'vuex';
import * as TeamActions from '@/store/actions/Team.actions';
import * as UserActions from '@/store/actions/User.actions';
import * as StandupActions from '@/store/actions/Standup.actions';
import * as GoalActions from '@/store/actions/Goal.actions';
import * as GoalMutations from '@/store/mutations/Goal.mutations';
import * as StandupMutations from '@/store/mutations/Standup.mutations';
import { isObjectEmpty, dictionaryFromDocumentArray, documentWithId } from '@/helpers/dataHelpers';
import { defaultDateOptions, dateToISO, getLastOccuringDayOfWeekFromDate, isUserOOO } from '@/helpers/dateHelpers';
import { getPresenterKeyIntent, PRESENTER_INTENT_NEXT, PRESENTER_INTENT_PREVIOUS } from '@/helpers/keyboardHelpers';
import { daysOfTheWeek } from '@/constants/TimeAndDateConstants';
import * as Types from '@/constants/Types';
import * as DatabaseService from '@/services/Database.service';
import * as collections from '@/constants/dbCollections';

import UserAvatar from '@/components/UserAvatar.vue';
import DailyGoal from '@/components/DailyGoal.vue';
import UserDisplayName from '@/components/UserDisplayName.vue';

// Gaps between each temporary order during the shuffle riffle. They grow so the scramble
// visibly slows before it settles. Keep the CSS .queue-move duration in step with these.
export const SHUFFLE_RIFFLE_DELAYS_MS = [120, 140, 170, 210, 260, 320];
// How long the first pending avatar pulses once the order has landed.
export const SHUFFLE_LANDED_PULSE_MS = 700;

export default {
  name: 'TeamStandupView',

  components: {
    UserAvatar,
    DailyGoal,
    UserDisplayName,
  },

  data() {
    return {
      // Temporary order shown in the footer while the shuffle animation riffles. null means
      // "show the persisted order". Navigation never reads this; see orderedPendingUsers.
      displayedOrderUserIds: null,
      isShuffling: false,
      landedUserId: null,
      shuffleAnimationTimeouts: [],
      shuffleAnimationTargetKey: null,
      standupTimerSeconds: 0,
      standupTimerMinutes: 0,
      isInitialDataFetching: true,
      isAdvancing: false,
      unsubscribeStandupListener: null,
      unsubscribeGoalsListeners: null,
      defaultDateOptions,
    };
  },

  computed: {
    ...mapState({
      teams: state => state.team.teams,
      users: state => state.user.users,
      standups: state => state.standup.standups,
      goals: state => state.goal.goals,
      loggedInUserId: state => state.auth.loggedInUserId,
    }),
    ...mapGetters([
      'activeWorkspace',
    ]),
    activeStandup() {
      if (isObjectEmpty(this.standups) || !this.$route.params.standupId) return {};

      return this.standups[this.$route.params.standupId];
    },
    activeTeam() {
      if (
        isObjectEmpty(this.teams)
        || !this.$route.params.standupId
        || isObjectEmpty(this.activeStandup)
      ) return {};

      return this.teams[this.activeStandup.teamId];
    },
    activeTeamUsers() {
      if (isObjectEmpty(this.activeTeam)) return [];

      return Object.values(this.users).filter(user => this.activeTeam.userIds.includes(user.id));
    },
    alreadyReportedUsers() {
      return this.activeTeamUsers.filter(user => this.activeStandup.alreadyReportedUserIds.includes(user.id));
    },
    usersPendingReport() {
      return this.activeTeamUsers.filter(user => !this.activeStandup.alreadyReportedUserIds.includes(user.id));
    },
    // The shared reporting order lives on the standup document so every viewer sees the same
    // lineup. Standups created before it existed fall back to team order.
    reportingOrderUserIds() {
      const teamUserIds = this.activeTeamUsers.map(user => user.id);
      const persistedOrder = this.activeStandup.reportingOrderUserIds;
      if (!Array.isArray(persistedOrder) || !persistedOrder.length) return teamUserIds;

      // Anyone added to the team after the order was drawn still needs a turn.
      const missingUserIds = teamUserIds.filter(id => !persistedOrder.includes(id));

      return missingUserIds.length ? [...persistedOrder, ...missingUserIds] : persistedOrder;
    },
    // Used by all navigation logic (next / back / keyboard). Always the real, persisted order.
    orderedPendingUsers() {
      return this.pendingUsersInOrder(this.reportingOrderUserIds);
    },
    // Used only by the footer template, so a running riffle never changes where "Next" goes.
    displayedPendingUsers() {
      if (!this.displayedOrderUserIds) return this.orderedPendingUsers;

      return this.pendingUsersInOrder(this.displayedOrderUserIds);
    },
    isLoading() {
      return this.isInitialDataFetching;
    },
    todayISO() {
      return dateToISO(new Date());
    },
    isCurrentReporterOOO() {
      const reporter = this.users[this.activeStandup.currentReportingUserId];
      return reporter ? isUserOOO(reporter, this.todayISO) : false;
    },
    // Only the person who launched the standup drives it from the keyboard, so a participant's
    // stray arrow key can't move the meeting on for everyone. Standups created before this
    // shipped have no launchedByUserId, so fall back to the team leader.
    isPresenter() {
      const presenterUserId = this.activeStandup.launchedByUserId || this.activeTeam.teamLeaderId;

      return Boolean(presenterUserId) && this.loggedInUserId === presenterUserId;
    },
    isLastReporter() {
      return !this.orderedPendingUsers
        .some(user => user.id !== this.activeStandup.currentReportingUserId);
    },
    nextReporterButtonLabel() {
      return this.isLastReporter ? 'Finish Standup' : 'Next Person';
    },
    teamGoalIds() {
      return Object.values(this.goals)
        .filter(goal => this.activeTeam.userIds.includes(goal.userId))
        .map(goal => goal.id);
    },
    // Function to find the current day, AND the most recent previous reporting day
    // based on the team's reporting settings.
    teamReportingDatesInISO() { // Returns dates as  'YYYY-MM-DD'
      if (isObjectEmpty(this.activeTeam)) return [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const teamReportingDaysForCurrentStandup = [];
      let sortedDays = null;

      if (this.activeTeam.standupReportingDayIds.includes(Types.TYPE_REPORTING_DAY_TODAY)) {
        teamReportingDaysForCurrentStandup.push(dateToISO(today));
      }

      // If team needs to report on 'yesterday's' goal then we need to find the actual previous day
      // based on the team settings. In many cases it is not truly yesterday, despite the product nomenclature.
      if (this.activeTeam.standupReportingDayIds.includes(Types.TYPE_REPORTING_DAY_YESTERDAY)) {
        const sortedTeamStandupDays = [...this.activeTeam.standupMeetingDayIds]
          .sort((a, b) => b - a)
          .map(dayId => daysOfTheWeek.find(day => day.id === dayId));

        const todayWeekDayIndex = today.getDay();
        const previousTeamStandupDay = this.findPreviousTeamReportingDay(sortedTeamStandupDays, todayWeekDayIndex);
        const previousDayDate = getLastOccuringDayOfWeekFromDate(today, previousTeamStandupDay.weekDayIndex);

        // If last occuring date function returns same date (due to range being outside 6 days)
        // Make sure we are referring to the PREVIOUS week. E.G: Friday --> Last Friday.
        if (previousDayDate.getTime() === today.getTime()) {
          previousDayDate.setDate(previousDayDate.getDate() - 7);
        }

        sortedDays = [dateToISO(previousDayDate), ...teamReportingDaysForCurrentStandup];
      }

      return sortedDays || teamReportingDaysForCurrentStandup;
    },
  },

  watch: {
    // A new order arriving from Firestore means someone (usually the presenter) hit Shuffle.
    // Riffle to it so every viewer sees the same draw land on the same lineup.
    'activeStandup.reportingOrderUserIds': function watchReportingOrder(nextOrder, previousOrder) {
      if (this.isInitialDataFetching || !Array.isArray(nextOrder) || !nextOrder.length) return;

      // Every standup write replaces the document in the store, so this fires with a fresh array
      // on each Next / Back. Only an actual change in order is a shuffle.
      const nextKey = nextOrder.join(',');
      const previousKey = Array.isArray(previousOrder) ? previousOrder.join(',') : null;
      if (nextKey === previousKey) return;
      // The presenter's own click already started this animation locally.
      if (nextKey === this.shuffleAnimationTargetKey) return;

      this.playShuffleAnimation(nextOrder);
    },
  },

  async created() {
    await this.fetchInitialStandupData();

    this.subscribeToStandupChangeEvents();
    this.subscribeToGoalsChangeEvents();
  },

  mounted() {
    window.addEventListener('keydown', this.handlePresenterKey);
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handlePresenterKey);
    this.cancelShuffleAnimation();
    this.unsubscribeStandupListener();
    this.unsubscribeGoalsListeners.map(goalQueryListener => goalQueryListener());
  },

  methods: {
    ...mapActions({
      fetchTeams: TeamActions.FETCH_TEAMS_BY_IDS,
      fetchWorkspaceUsersByIds: UserActions.FETCH_WORKSPACE_USERS_BY_IDS,
      fetchGoalsByUserIds: GoalActions.FETCH_GOALS_BY_USER_IDS,
      fetchStandupsByIds: StandupActions.FETCH_STANDUPS_BY_IDS,
      updateStandup: StandupActions.UPDATE_STANDUP,
    }),
    ...mapMutations({
      receiveGoals: GoalMutations.RECEIVE_GOALS,
      receiveStandups: StandupMutations.RECEIVE_STANDUPS,
    }),
    handleStandupUpdate(standupSnapshot) {
      const shapedDocument = documentWithId(standupSnapshot);
      this.receiveStandups({ standups: { [shapedDocument.id]: shapedDocument } });
    },
    handleGoalUpdate(goalsQuerySnapshot) {
      if (goalsQuerySnapshot.docChanges().length > 0) {
        const updatedGoalDocuments = goalsQuerySnapshot.docChanges().map(change => change.doc);
        const shapedGoalDocuments = dictionaryFromDocumentArray(updatedGoalDocuments);

        this.receiveGoals(shapedGoalDocuments);
      }
    },
    handleClickLeaveStandup() {
      this.$router.push('/dashboard');
    },
    handleClickEndReporting() {
      this.updateStandup({
        standupId: this.activeStandup.id,
        updatedStandup: { hasReportingStarted: false, isStandupReportingComplete: true },
      });
    },
    // Wraps every standup write triggered by next/back so rapid clicker presses can't race each
    // other — UPDATE_STANDUP writes to Firestore *and* re-fetches on top of the snapshot listener.
    async runStandupNavigation(updatedStandup) {
      if (this.isAdvancing) return;

      this.isAdvancing = true;
      try {
        await this.updateStandup({ standupId: this.activeStandup.id, updatedStandup });
      } finally {
        this.isAdvancing = false;
      }
    },
    advanceToNextReporter() {
      const currentUserId = this.activeStandup.currentReportingUserId;
      // Filter first so re-selecting an earlier reporter can't leave duplicate ids behind —
      // goToPreviousReporter walks this array one entry at a time.
      const alreadyReportedUserIds = [
        ...this.activeStandup.alreadyReportedUserIds.filter(userId => userId !== currentUserId),
        currentUserId,
      ];
      const nextPendingUser = this.orderedPendingUsers.find(u => u.id !== currentUserId);

      if (nextPendingUser) {
        return this.runStandupNavigation({
          currentReportingUserId: nextPendingUser.id,
          alreadyReportedUserIds,
        });
      }

      return this.runStandupNavigation({
        alreadyReportedUserIds,
        hasReportingStarted: false,
        isStandupReportingComplete: true,
      });
    },
    goToPreviousReporter() {
      const alreadyReportedUserIds = [...this.activeStandup.alreadyReportedUserIds];
      const previousUserId = alreadyReportedUserIds.pop();

      if (!previousUserId) return undefined;

      // The outgoing speaker isn't in alreadyReportedUserIds, so they return to the pending queue
      // in their original position. hasReportingStarted/isStandupReportingComplete reset so this
      // also recovers from advancing one person too far off the end.
      return this.runStandupNavigation({
        currentReportingUserId: previousUserId,
        alreadyReportedUserIds,
        hasReportingStarted: true,
        isStandupReportingComplete: false,
      });
    },
    handleClickSkipOOO() {
      return this.advanceToNextReporter();
    },
    handlePresenterKey(event) {
      if (!this.isPresenter || this.isLoading || this.isAdvancing) return;

      const intent = getPresenterKeyIntent(event);
      if (!intent) return;

      event.preventDefault(); // Stop PageDown / arrow keys scrolling the standup modal.

      if (this.activeStandup.isStandupReportingComplete) {
        // Never leave the standup on a keypress — only allow backing out of the completed state.
        if (intent === PRESENTER_INTENT_PREVIOUS) this.goToPreviousReporter();
        return;
      }

      if (!this.activeStandup.hasReportingStarted) {
        if (intent === PRESENTER_INTENT_NEXT) this.handleClickBeginReporting();
        return;
      }

      if (intent === PRESENTER_INTENT_NEXT) this.advanceToNextReporter();
      else this.goToPreviousReporter();
    },
    isUserOOO,
    handleClickBeginReporting() {
      this.updateStandup({
        standupId: this.activeStandup.id,
        updatedStandup: { hasReportingStarted: true },
      });
    },
    // Recursive function to iterate backward through team standup days and find the closest
    // weekday that matches.
    findPreviousTeamReportingDay(sortedTeamStandupDays, todayWeekDayIndex) {
      if (!sortedTeamStandupDays.find(day => day.weekDayIndex === todayWeekDayIndex - 1) && todayWeekDayIndex > 0) {
        return this.findPreviousTeamReportingDay(sortedTeamStandupDays, todayWeekDayIndex - 1);
      }
      // If it's a sunday (0), reset weekday value back to saturday (6) instead of going negative.
      if (todayWeekDayIndex === 0) {
        return this.findPreviousTeamReportingDay(sortedTeamStandupDays, 6);
      }

      return sortedTeamStandupDays.find(day => day.weekDayIndex === todayWeekDayIndex - 1);
    },
    /* handleClickBeginReporting() {
      this.startTimer();
    },
    startTimer() {
      this.standupTimerInterval = setInterval(() => {
        if (this.standupTimerSeconds < 59) {
          this.standupTimerSeconds = this.standupTimerSeconds + 1;
        } else if (this.standupTimerSeconds === 59) {
          this.standupTimerMinutes = this.standupTimerMinutes + 1;
          this.standupTimerSeconds = 0;
        }
      }, 1000);
    }, */
    shuffleUserIds(ids) {
      const shuffled = [...ids];
      for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },
    pendingUsersInOrder(orderedUserIds) {
      const pendingIds = new Set(this.usersPendingReport.map(user => user.id));

      return orderedUserIds
        .filter(id => pendingIds.has(id))
        .map(id => this.users[id])
        .filter(Boolean);
    },
    prefersReducedMotion() {
      return Boolean(
        typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      );
    },
    cancelShuffleAnimation() {
      this.shuffleAnimationTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      this.shuffleAnimationTimeouts = [];
      this.shuffleAnimationTargetKey = null;
      this.displayedOrderUserIds = null;
      this.isShuffling = false;
      this.landedUserId = null;
    },
    // "Riffle and settle": show a few temporary random orders at increasing delays so the
    // avatars visibly scramble, slow down, and stop on the real order. The intermediate orders
    // are random per client; only finalOrder is shared, so every viewer lands on the same lineup.
    playShuffleAnimation(finalOrder) {
      this.cancelShuffleAnimation();

      const pendingIds = this.pendingUsersInOrder(finalOrder).map(user => user.id);
      if (pendingIds.length < 2 || this.prefersReducedMotion()) return;

      this.shuffleAnimationTargetKey = finalOrder.join(',');
      this.isShuffling = true;

      let elapsed = 0;
      SHUFFLE_RIFFLE_DELAYS_MS.forEach((delay) => {
        elapsed += delay;
        this.shuffleAnimationTimeouts.push(setTimeout(() => {
          this.displayedOrderUserIds = this.shuffleUserIds(pendingIds);
        }, elapsed));
      });

      elapsed += SHUFFLE_RIFFLE_DELAYS_MS[SHUFFLE_RIFFLE_DELAYS_MS.length - 1];
      this.shuffleAnimationTimeouts.push(setTimeout(() => {
        this.displayedOrderUserIds = null;
        this.isShuffling = false;
        [this.landedUserId] = pendingIds;
      }, elapsed));

      // shuffleAnimationTargetKey deliberately stays set until the next shuffle, so a slow
      // Firestore echo of the presenter's own click can never replay the riffle.
      this.shuffleAnimationTimeouts.push(setTimeout(() => {
        this.landedUserId = null;
      }, elapsed + SHUFFLE_LANDED_PULSE_MS));
    },
    handleClickShuffle() {
      if (this.isShuffling) return;

      const alreadyReportedUserIds = [...this.activeStandup.alreadyReportedUserIds];
      const shuffledPendingIds = this.shuffleUserIds(this.usersPendingReport.map(user => user.id));
      const reportingOrderUserIds = [...alreadyReportedUserIds, ...shuffledPendingIds];

      // Start locally so the presenter gets instant feedback; the watcher skips the echo.
      this.playShuffleAnimation(reportingOrderUserIds);
      this.updateStandup({
        standupId: this.activeStandup.id,
        updatedStandup: {
          reportingOrderUserIds,
          currentReportingUserId: shuffledPendingIds[0],
        },
      });
    },
    handleSelectReportingUser(userId) {
      const alreadyReportedUserIds = [...this.activeStandup.alreadyReportedUserIds];
      const updatedUserIds = alreadyReportedUserIds
        .filter(alreadyReportedUserId => userId !== alreadyReportedUserId);

      updatedUserIds.push(this.activeStandup.currentReportingUserId);

      this.updateStandup({
        standupId: this.activeStandup.id,
        updatedStandup: {
          currentReportingUserId: userId,
          alreadyReportedUserIds: [...updatedUserIds],
        },
      });
    },
    async subscribeToStandupChangeEvents() {
      this.unsubscribeStandupListener = await DatabaseService
        .getDocumentReference(collections.STANDUPS, this.activeStandup.id)
        .onSnapshot(this.handleStandupUpdate);
    },
    async subscribeToGoalsChangeEvents() {
      this.unsubscribeGoalsListeners = await Promise.all(
        this.activeTeam.userIds
          .map(userId => DatabaseService.getGoalsRefsByUserId(userId, this.activeWorkspace.id).onSnapshot(this.handleGoalUpdate)),
      );
    },
    async fetchInitialStandupData() {
      return new Promise(async (resolve) => {
        await this.fetchStandupsByIds({ standupIds: [this.$route.params.standupId] });
        await this.fetchTeams({ teamIds: [this.activeStandup.teamId] });
        await this.fetchWorkspaceUsersByIds({
          userIds: [...this.activeTeam.userIds],
          workspaceId: this.activeWorkspace.id,
        });

        // The first person to open the standup draws the shared reporting order. Anyone joining
        // later reads it from the document, so everyone sees the same lineup.
        const persistedOrder = this.activeStandup.reportingOrderUserIds;
        const hasPersistedOrder = Array.isArray(persistedOrder) && persistedOrder.length > 0;
        const updatedStandup = {};

        if (!hasPersistedOrder) {
          updatedStandup.reportingOrderUserIds = this.shuffleUserIds(this.activeTeamUsers.map(u => u.id));
        }
        if (!this.activeStandup.currentReportingUserId) {
          const order = updatedStandup.reportingOrderUserIds || persistedOrder;
          [updatedStandup.currentReportingUserId] = order;
        }
        if (Object.keys(updatedStandup).length) {
          await this.updateStandup({ standupId: this.activeStandup.id, updatedStandup });
        }

        await this.fetchGoalsByUserIds({ userIds: [...this.activeTeam.userIds], workspaceId: this.activeWorkspace.id });

        this.isInitialDataFetching = false;
        // Play the draw on open so the team sees the order being shuffled, not just appearing.
        this.playShuffleAnimation(this.reportingOrderUserIds);
        resolve();
      });
    },
  },
};
</script>

<style lang="scss">
.team-standup__body {
  background-color: $skwad-light-grey;
}

.standup__modal-background {
  & .modal {
    background-color: transparent!important;
  }
}

.team-standup__navigation {
  box-shadow: $light-shadow;
}

.modal-card-body {
  background-color: $body-background-color;
  margin-top: $navbar-height;
}

.team-standup__footer {
  position: fixed;
  display: flex;
  justify-content: space-between;
  bottom: 0;
  width: 100%;
  background-color: $white;
  min-height: $navbar-height + $global-whitespace-base;
}

.team-standup__footer__section {
  height: $navbar-height + $global-whitespace-base;
  padding: 0 $global-whitespace-base;
  display: flex;

  & > div {
    align-self: center;
  }
}

.team-standup__footer__timer__wrapper {
  box-shadow: 6px 0 10px -2px $grey-lighter;
  min-width: 100px;
}

.team-standup__footer__timer {
  font-size: $size-5;
  font-weight: $weight-bold;
  color: $primary;
}

.team-standup__footer__users-queue__wrapper {
  display: flex;
  align-items: center;
  padding: 0 $global-whitespace-base;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
}

.team-standup__footer__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 0 $global-whitespace-base;
}

.team-standup__footer__end {
  display: flex;
  justify-content: flex-end;
  align-self: center;
}

.team-standup__footer__clicker-hint {
  margin-top: $global-whitespace-small;
  color: $grey;
  font-size: 0.75rem;
}

.users-queue__avatar {
  position: relative;
  margin-right: $global-whitespace-small;
  cursor: pointer;

   > span { // Selects the icon wrapper.
     height: 0;
     position: absolute;
     top: -9px;
     left: 7px;
  }
}

.users-queue__reporting-icon {
  position: absolute;
}

.users-queue__list {
  display: flex;
  align-items: center;
}

// Vue transition-group FLIP: avatars glide to their new slot on any reorder. Duration sits
// just under the shortest riffle step so each scramble finishes before the next one starts.
.queue-move {
  transition: transform 0.28s cubic-bezier(0.075, 0.82, 0.165, 1);
}

@keyframes users-queue-land {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba($primary, 0.55); }
  40% { transform: scale(1.18); box-shadow: 0 0 0 6px rgba($primary, 0.25); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba($primary, 0); }
}

.users-queue__avatar--landed .user-avatar__wrapper {
  border-radius: 50%;
  animation: users-queue-land 0.7s cubic-bezier(0.075, 0.82, 0.165, 1) both;
}

@keyframes users-queue-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.users-queue__shuffle-button {
  align-self: center;
  margin-right: $global-whitespace-xsmall;

  &.is-shuffling .icon {
    animation: users-queue-spin 0.6s linear infinite;
  }
}

@media (prefers-reduced-motion: reduce) {
  .queue-move,
  .users-queue__avatar--landed .user-avatar__wrapper,
  .users-queue__shuffle-button.is-shuffling .icon {
    transition: none;
    animation: none;
  }
}

.users-queue__divider {
  padding: 0 $global-whitespace-xsmall;
  border-left: 1px solid $grey-light;
  height: 100%;

  &:after {
    content: ' ';
  }
}

.users-queue__ooo-badge {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 8px;
  font-weight: $weight-bold;
  color: $white;
  background-color: $warning;
  border-radius: 2px;
  padding: 0 2px;
  white-space: nowrap;
  pointer-events: none;
}
</style>
